'use client';

import { Box } from '@mui/material';

import { CategoryChip, CategoryChipSkeleton } from '@/components';
import type { BreakingNewsCategory } from '@/types/breaking.types';

interface BreakingCategoriesProps {
  categories: BreakingNewsCategory[];
  selectedTagId: string | null;
  onCategoryClick: (tagId: string | null) => void;
  isLoading?: boolean;
}

export function BreakingCategories({
  categories,
  selectedTagId,
  onCategoryClick,
  isLoading = false,
}: BreakingCategoriesProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 3,
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          overflowX: { xs: 'auto', sm: 'visible' },
          overflowY: 'hidden',
          pb: { xs: 1, sm: 0 },
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <CategoryChipSkeleton />
      </Box>
    );
  }

  const renderCategory = (category: BreakingNewsCategory) => {
    // "All" is the only entry with a null tagId by design (see useBreaking's
    // synthetic allCategory) — comparing by tagId alone would also light up
    // any real category the API returns without a tagId, so "All" is keyed
    // off its slug and every other chip additionally requires a real tagId.
    const isSelected =
      category?.slug === 'all'
        ? selectedTagId === null
        : selectedTagId != null && selectedTagId === category.tagId;

    return (
      <CategoryChip
        key={category?.tagId ?? 'all'}
        label={category?.name || ''}
        isActive={isSelected}
        onClick={() => onCategoryClick(category?.tagId)}
      />
    );
  };

  // Drop real categories whose tag lookup failed server-side (tagId null) —
  // clicking one would be indistinguishable from clicking "All" since both
  // filter on a null tag_id, so they're not renderable as a distinct filter.
  const renderableCategories = categories?.filter(
    (category) => category?.slug === 'all' || category?.tagId != null,
  );

  const renderCategoriesList = renderableCategories?.map(renderCategory);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 3,
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        overflowX: { xs: 'auto', sm: 'visible' },
        overflowY: 'hidden',
        pb: { xs: 1, sm: 0 },
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {renderCategoriesList}
    </Box>
  );
}
